import { Link } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { displayDate } from "src/utils/dateHandler.util";
import { BillingException, BillingExceptionIssueCode } from "../types/billing-exception.entity";
import {
  isStoreSubscriptionProvider,
  StoreSubscriptionVerificationTarget,
  VerifyStoreSubscriptionButton,
} from "src/features/subscription-users/components/store-subscription-verification";

type Props = {
  data: BillingException[];
  isDarkMode: boolean;
  isVerifyingStoreSubscription: boolean;
  onVerifyStoreSubscription: (target: StoreSubscriptionVerificationTarget) => void;
};

const issueLabels: Record<BillingExceptionIssueCode, string> = {
  CHECKOUT_NOT_COMPLETED: "Checkout uncompleted for over 1 hour",
  ACTIVE_SUBSCRIPTION_PAST_END_DATE: "Still active after end date",
  ACTIVE_SUBSCRIPTION_POINTER_MISMATCH: "Active subscription link differs",
  USER_RECORD_MISSING: "User record missing",
};

const issueClass = (issueCode: BillingExceptionIssueCode, isDarkMode: boolean) => {
  if (issueCode === "USER_RECORD_MISSING") {
    return isDarkMode
      ? "border-rose-500/30 bg-rose-500/10 text-rose-200"
      : "border-rose-200 bg-rose-50 text-rose-700";
  }

  return isDarkMode
    ? "border-amber-400/25 bg-amber-400/10 text-amber-100"
    : "border-amber-200 bg-amber-50 text-amber-800";
};

const formatPaymentAmount = (exception: BillingException) => {
  const payment = exception.payment;
  if (!payment) return "-";

  return `${payment.paymentAmount} ${payment.paymentCurrency}`;
};

export const BillingExceptionsTable = ({
  data,
  isDarkMode,
  isVerifyingStoreSubscription,
  onVerifyStoreSubscription,
}: Props) => (
  <div className={`overflow-x-auto border ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
    <table className="min-w-[920px] w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
      <thead className={isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-900 text-white"}>
        <tr className="text-xs uppercase tracking-wide">
          <th className="px-4 py-3 font-semibold">Exception</th>
          <th className="px-4 py-3 font-semibold">User</th>
          <th className="px-4 py-3 font-semibold">Record</th>
          <th className="px-4 py-3 font-semibold">Relevant date</th>
          <th className="px-4 py-3 font-semibold text-right">Open</th>
        </tr>
      </thead>
      <tbody className={isDarkMode ? "divide-y divide-slate-800" : "divide-y divide-slate-100"}>
        {data.map((exception) => {
          const recordLabel = exception.scope === "payments" ? "Payment transaction" : "Subscription";
          const recordPath =
            exception.scope === "payments"
              ? `/payment-transactions/${exception.recordId}`
              : `/subscription-users/${exception.userId}`;
          const provider = exception.subscription?.provider;
          const storeProvider = isStoreSubscriptionProvider(provider)
            ? provider
            : undefined;

          return (
            <tr
              key={exception.id}
              className={isDarkMode ? "bg-slate-900/50 hover:bg-slate-800/80" : "bg-white hover:bg-slate-50"}
            >
              <td className="px-4 py-4 align-top">
                <div className="flex max-w-[270px] flex-wrap gap-1.5">
                  {exception.issueCodes.map((issueCode) => (
                    <span
                      key={issueCode}
                      className={`rounded border px-2 py-1 text-xs font-medium ${issueClass(issueCode, isDarkMode)}`}
                    >
                      {issueLabels[issueCode]}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-4 align-top">
                <Link
                  to={`/users/${exception.userId}`}
                  className={`block max-w-[220px] no-underline ${
                    isDarkMode ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-900"
                  }`}
                >
                  <span className="block truncate font-semibold">{exception.user?.name ?? exception.userId}</span>
                  <span className={`block truncate text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {exception.user?.email ?? exception.userId}
                  </span>
                </Link>
              </td>
              <td className="px-4 py-4 align-top">
                <div className={isDarkMode ? "text-slate-100" : "text-slate-800"}>
                  <span className="block font-semibold">{recordLabel}</span>
                  <span className={`block text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                    {exception.scope === "payments"
                      ? `${exception.payment?.PaymentService.replace("_", " ")} - ${formatPaymentAmount(exception)}`
                      : `${exception.subscription?.provider} - ${exception.subscription?.plan}`}
                  </span>
                  <span className={`block max-w-[180px] truncate font-mono text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                    {exception.recordId}
                  </span>
                </div>
              </td>
              <td className="px-4 py-4 align-top">
                <span className={isDarkMode ? "text-slate-200" : "text-slate-700"}>{displayDate(exception.detectedAt)}</span>
              </td>
              <td className="px-4 py-4 text-right align-top">
                <div className="flex justify-end gap-2">
                  {exception.scope === "subscriptions" && storeProvider && (
                    <VerifyStoreSubscriptionButton
                      isDarkMode={isDarkMode}
                      label="Verify"
                      isLoading={isVerifyingStoreSubscription}
                      onVerify={() =>
                        onVerifyStoreSubscription({
                          userId: exception.userId,
                          subscriptionId: exception.recordId,
                          provider: storeProvider,
                        })
                      }
                    />
                  )}
                  <Link
                    to={recordPath}
                    title={`Open ${recordLabel.toLowerCase()}`}
                    aria-label={`Open ${recordLabel.toLowerCase()}`}
                    className={`inline-flex h-9 w-9 items-center justify-center rounded border transition-colors ${
                      isDarkMode
                        ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15 hover:text-sky-200"
                        : "border-slate-200 text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
                    }`}
                  >
                    <FaExternalLinkAlt className="text-sm" />
                  </Link>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
